import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { schemaGraph } from '../../utils/schema';
import { getStateBySlug } from '../../utils/states';

const KS = getStateBySlug('kansas');

const UPDATED_LABEL = 'Updated September 2026 · Verified against krec.ks.gov';

// Answers are rendered on the page under "Frequently asked questions" as well —
// Google expects marked-up FAQ answers to be visible content, not schema-only.
const FAQ_SCHEMA = {
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How many CE hours does Kansas require for real estate license renewal?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '12 hours every two-year renewal cycle to renew on active status, including at least 3 hours in an approved course titled Kansas Required Core. The same 12 hours apply to salespersons and brokers.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the Kansas Required Core course?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A 3-hour course approved by the Kansas Real Estate Commission that follows the topical outline KREC publishes for each cycle. It is mandatory every renewal. A course called "core" in another state does not satisfy it.'
      }
    },
    {
      '@type': 'Question',
      name: 'Does my school report my CE to KREC, or do I?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Your school does. Kansas schools must report course completion data to KREC within five calendar days of the completion date, or by your license expiration date, whichever comes first. This is the opposite of states like Arizona, where the licensee self-reports.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I complete Kansas real estate CE online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. KREC approves asynchronous distance education, and courses may be completed any time within the two-year renewal period. Distance courses must include at least 10 quiz or exam questions per credit hour, and you must answer at least 90% correctly.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the Kansas real estate license renewal fee?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '$125 for a salesperson and $175 for a broker, for the two-year renewal. A $100 late fee applies to any renewal submitted after the expiration date.'
      }
    },
    {
      '@type': 'Question',
      name: 'What happens if my Kansas real estate license expires?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An expired license must be renewed within six months of expiration or it is forfeited. KREC sends a courtesy renewal reminder email 45 days before your expiration date.'
      }
    },
    {
      '@type': 'Question',
      name: 'Does the REALTOR Code of Ethics count for Kansas CE?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'KREC does not require it, and it is not a core course. A KREC-approved Code of Ethics course can count as elective credit toward your 12 hours, but it never replaces the Kansas Required Core. REALTOR associations require it separately for membership.'
      }
    }
  ]
};

const RELATED = [
  { url: '/kansas/resources', title: 'All Kansas CE Guides', desc: 'Quick facts, answers to common questions, and every Kansas guide in one place.' },
  { url: '/kansas/courses', title: 'Kansas CE Courses', desc: 'All four KREC-approved courses, or the complete 12-hour package.' },
  { url: '/kansas/how-to-renew', title: 'How to Renew Your Kansas License', desc: 'The renewal steps end to end, including what we file for you.' },
];

export default function KansasCeRequirementsPage() {
  useSEO({
    title: 'Kansas Real Estate CE Requirements 2026 | Complete 12-Hour Guide',
    description: 'Every KREC continuing education requirement explained: the 12-hour breakdown, the 3-hour Kansas Required Core, deadlines and what counts.',
    url: canonicalUrl('/kansas-real-estate-ce-requirements'),
    jsonLd: schemaGraph({
      mainSchemas: [FAQ_SCHEMA],
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: 'Kansas', url: 'https://steadyeducation.com/kansas' },
        { name: 'Resources', url: 'https://steadyeducation.com/kansas/resources' },
        { name: 'CE Requirements', url: canonicalUrl('/kansas-real-estate-ce-requirements') },
      ],
    }),
  });

  return (
    <ResourceLayout
      state={KS}
      crumbs={[{ label: 'Kansas', href: '/kansas' }, { label: 'Resources', href: '/kansas/resources' }]}
      updatedLabel={UPDATED_LABEL}
      ctaSub="All 12 KREC-required hours"
      ctaPoints={[
        'The mandatory 3-hour Required Core',
        'Three 3-hour electives',
        '100% online, self-paced',
        'We report your completion to KREC',
      ]}
      breadcrumb="CE Requirements"
      title="Kansas Real Estate CE Requirements: The Complete 2026 Guide"
      subtitle="Kansas real estate licensees must complete 12 hours of KREC-approved continuing education every two-year renewal cycle, including a mandatory 3-hour Kansas Required Core. Unlike some states, your school files the completion with the Commission — not you."
      relatedGuides={RELATED}
    >
      <h2>How many CE hours does Kansas require?</h2>
      <p>Salespersons and brokers both need <strong>12 hours</strong> of KREC-approved continuing education to renew on active status. At least 3 of those hours must be an approved course titled <strong>Kansas Required Core</strong>. The remaining 9 hours are electives.</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr><th>Requirement</th><th style={thRight}>Hours</th></tr>
          </thead>
          <tbody>
            <tr><td>Kansas Required Core (mandatory)</td><td style={tdRight}>3</td></tr>
            <tr><td>Electives (any KREC-approved courses)</td><td style={tdRight}>9</td></tr>
            <tr style={totalRow}><td>Total per renewal cycle</td><td style={tdRight}>12</td></tr>
          </tbody>
        </table>
      </div>
      <p>Kansas does not split the elective hours into fixed categories the way some states do, so the 9 hours can be any approved courses — with one exception noted below about appraisal courses.</p>

      <h2>What is the Kansas Required Core?</h2>
      <p>The Required Core is a 3-hour course that follows a topical outline KREC publishes and approves. It is mandatory for every licensee, every renewal cycle, and it is the part most often missed by people who buy hours from an out-of-state provider.</p>
      <p><strong>A course titled "core" in another state does not count in Kansas.</strong> The course has to be specifically Kansas-approved and carry a KREC course number. If you hold licenses in more than one state, plan on separate CE for each.</p>

      <h2>Can you complete Kansas CE online?</h2>
      <p>Yes. KREC approves asynchronous distance education — self-paced online courses with no scheduled class time — and courses may be completed any time within your two-year renewal period.</p>
      <p>Distance education carries one rule worth knowing: under K.A.R. 86-1-10, a course must include <strong>at least 10 quiz or exam questions per credit hour</strong>, and you must answer <strong>at least 90% correctly</strong> to receive credit. That is why a 3-hour Kansas course ends in a substantial final exam rather than a short quiz.</p>

      <h2>Who reports your hours to KREC?</h2>
      <p><strong>Your school does, not you.</strong> Kansas schools are required to report course completion data to KREC within <strong>five calendar days</strong> of the completion date, or by the licensee's expiration date, whichever comes sooner. Schools upload the data through KREC's online portal.</p>
      <p>This is the single biggest practical difference between Kansas and states like Arizona, where the licensee has to enter every course into a state portal themselves. In Kansas there is nothing for you to file — but it does mean you should not leave your CE until the final day or two, because the school still needs time to report it before your license expires.</p>
      <p>You will still receive a Commission-approved certificate for your own records after each course.</p>

      <h2>When is your renewal deadline?</h2>
      <p>A renewed Kansas license expires <strong>two years after the expiration date of the preceding license</strong>. KREC emails a courtesy renewal reminder <strong>45 days before</strong> your expiration date, and CE may be completed at any point during the two-year cycle. KREC recommends finishing your CE at least <strong>7 days before</strong> expiration to avoid processing delays.</p>
      <p>A practical timeline:</p>
      <ul>
        <li><strong>60 days out:</strong> buy your package and start the Required Core.</li>
        <li><strong>14 days out:</strong> finish all 12 hours, leaving room for your school's five-day reporting window.</li>
        <li><strong>Before your expiration date:</strong> renew through the KREC Public Portal and pay the fee.</li>
      </ul>

      <h2>What does renewal cost?</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr><th>Item</th><th style={thRight}>Fee</th></tr>
          </thead>
          <tbody>
            <tr><td>Salesperson renewal (2 years)</td><td style={tdRight}>$125</td></tr>
            <tr><td>Broker renewal (2 years)</td><td style={tdRight}>$175</td></tr>
            <tr><td>Late fee (renewal after expiration)</td><td style={tdRight}>$100</td></tr>
          </tbody>
        </table>
      </div>

      <h2>What happens if your license expires?</h2>
      <p>An expired Kansas license must be renewed <strong>within six months</strong> of expiration, with the $100 late fee on top of the renewal fee. Miss that six-month window and the license is forfeited — at that point renewal is no longer an option and you would have to qualify again as a new applicant.</p>

      <h2>What about inactive status?</h2>
      <p>CE is not required to renew on <strong>inactive</strong> status. The hours do not disappear, though: they have to be made up before the license can be reactivated. Renewing inactive defers the requirement rather than removing it.</p>

      <h2>Does the REALTOR® Code of Ethics count?</h2>
      <p>KREC does not require Code of Ethics training — that is a REALTOR® association membership rule. But KREC treats it as an <strong>elective</strong>: a KREC-approved Code of Ethics course can count toward your 9 elective hours. It can never stand in for the Kansas Required Core. If you take it for your association, check that they accept the KREC-approved version.</p>

      <h2>Common mistakes that cost Kansas licensees time</h2>
      <ul>
        <li><strong>Buying "core" from an out-of-state provider.</strong> Only a KREC-approved Kansas Required Core satisfies the mandatory 3 hours.</li>
        <li><strong>Finishing CE on renewal day.</strong> Courses completed the same day a renewal is processed apply to the cycle you just closed, not the new one. KREC advises waiting 24 hours after your renewal is processed before starting CE meant for the next cycle.</li>
        <li><strong>Leaving no room for reporting.</strong> Your school has up to five calendar days to file, and the filing has to land before your expiration date.</li>
        <li><strong>Stacking appraisal courses.</strong> Licensees are limited to a single three-hour appraisal-type course per renewal period.</li>
        <li><strong>Renewing inactive and forgetting.</strong> The hours still have to be made up before you can go active again.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      {FAQ_SCHEMA.mainEntity.map(q => (
        <div key={q.name} style={{ marginBottom: 18 }}>
          <h3 style={faqQ}>{q.name}</h3>
          <p style={{ margin: 0 }}>{q.acceptedAnswer.text}</p>
        </div>
      ))}

      <div style={inlineCta}>
        <strong>Complete your Kansas CE with Steady Education.</strong> All 12 KREC-required hours — the mandatory Required Core plus three electives — for $39.99, or $14.99 per course. Self-paced, online, and we report your completion to KREC for you. Kansas School ID ED0190.
        <div style={{ marginTop: 14 }}>
          <Link to="/kansas/courses" style={inlineCtaBtn}>See the Kansas CE package →</Link>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 32 }}>
        Sources: <a href="https://www.krec.ks.gov/licensees/license-renewal/continuing-education" target="_blank" rel="noreferrer">KREC continuing education</a> and <a href="https://www.krec.ks.gov/licensees/license-renewal" target="_blank" rel="noreferrer">KREC license renewal</a>, September 2026. Fees and rules change — always confirm your own requirements with the Kansas Real Estate Commission.
      </p>
    </ResourceLayout>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 15, border: '1px solid #e8e2d6', borderRadius: 8, overflow: 'hidden' };
const thRight = { textAlign: 'right' };
const tdRight = { textAlign: 'right' };
const totalRow = { background: '#FAF7F2', fontWeight: 700 };
const faqQ = { fontSize: 16, fontWeight: 700, color: 'var(--forest-700)', margin: '0 0 6px' };
const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
